/**
 * The news board: what was added, changed and fixed, newest first. Every entry's words live in i18n:
 * `news.<id>.title` and `news.<id>.intro` for the release, `news.<id>.<item>` and `news.<id>.<item>.desc`
 * for each line. Add one entry per deploy that players would notice (CLAUDE.md, "Cómo trabajar").
 */
export type NewsTag = 'new' | 'improved' | 'balance' | 'fixed';
export const NEWS_TAGS: NewsTag[] = ['new', 'improved', 'balance', 'fixed'];

export interface NewsItem {
  id: string;
  tag: NewsTag;
}

export interface NewsEntry {
  /**
   * The date it went out (YYYY-MM-DD), with a `-name` suffix for a second release the same day; ids sort
   * as release order, newest first.
   */
  id: string;
  /** The headline item, shown large. */
  lead?: string;
  items: NewsItem[];
}

export const NEWS: NewsEntry[] = [
  {
    id: '2026-11-05-zcrypt',
    items: [
      { id: 'spectres', tag: 'fixed' },
      { id: 'chest', tag: 'fixed' },
    ],
  },
  {
    id: '2026-11-05-saberes',
    items: [
      { id: 'lore', tag: 'new' },
      { id: 'branchesDrawn', tag: 'fixed' },
    ],
  },
  {
    id: '2026-11-04-zzzzzzzzzzzzzzzzzzzzwalls',
    items: [{ id: 'walls', tag: 'fixed' }],
  },
  {
    id: '2026-11-04-zzzzzzzzzzzzzzzzzzzgoal',
    items: [{ id: 'goal', tag: 'improved' }],
  },
  {
    id: '2026-11-04-zzzzzzzzzzzzzzzzzzzcards',
    items: [
      { id: 'cards', tag: 'fixed' },
      { id: 'still', tag: 'improved' },
    ],
  },
  {
    id: '2026-11-04-zzzzzzzzzzzzzzzzzzhawk',
    items: [{ id: 'hawk', tag: 'improved' }],
  },
  {
    id: '2026-11-04-zzzzzzzzzzzzzzzzzartmage',
    items: [
      { id: 'artificer', tag: 'new' },
      { id: 'mage', tag: 'new' },
    ],
  },
  {
    id: '2026-11-04-zzzzzzzzzzzzzzzzrangcler',
    items: [
      { id: 'ranger', tag: 'new' },
      { id: 'cleric', tag: 'new' },
    ],
  },
  {
    id: '2026-11-04-zzzzzzzzzzzzzzzallies',
    items: [{ id: 'allyRoom', tag: 'fixed' }],
  },
  {
    id: '2026-11-04-zzzzzzzzzzzzzzbarbpal',
    items: [
      { id: 'barbarian', tag: 'new' },
      { id: 'paladin', tag: 'new' },
    ],
  },
  {
    id: '2026-11-04-zzzzzzzzzzzzzdruid',
    items: [{ id: 'druid', tag: 'new' }],
  },
  {
    id: '2026-11-04-zzzzzzzzzzzzglyphs',
    items: [{ id: 'deepGlyphs', tag: 'fixed' }],
  },
  {
    id: '2026-11-04-zzzzzzzzzzzpanoply',
    lead: 'panoply',
    items: [
      { id: 'panoply', tag: 'new' },
      { id: 'scraps', tag: 'new' },
      { id: 'infuse', tag: 'new' },
      { id: 'collection', tag: 'improved' },
    ],
  },
  {
    id: '2026-11-04-zzzzzzzzzzsignals',
    items: [{ id: 'signals', tag: 'improved' }],
  },
  {
    id: '2026-11-04-zzzzzzzzzwalls',
    items: [{ id: 'walls', tag: 'balance' }],
  },
  {
    id: '2026-11-04-zzzzzzzzaura',
    items: [{ id: 'aura', tag: 'improved' }, { id: 'timers', tag: 'improved' }],
  },
  {
    id: '2026-11-04-zzzzzzzring',
    items: [{ id: 'ring', tag: 'fixed' }],
  },
  {
    id: '2026-11-04-zzzzzzbagforge',
    items: [{ id: 'bagforge', tag: 'improved' }],
  },
  {
    id: '2026-11-04-zzzzzfrost',
    lead: 'frost',
    items: [
      { id: 'frost', tag: 'new' },
      { id: 'mended', tag: 'fixed' },
    ],
  },
  {
    id: '2026-11-04-zzzzprep',
    lead: 'prep',
    items: [
      { id: 'prep', tag: 'new' },
      { id: 'lobby', tag: 'fixed' },
      { id: 'fire', tag: 'fixed' },
      { id: 'pinned', tag: 'fixed' },
    ],
  },
  {
    id: '2026-11-04-zzzskies',
    lead: 'skies',
    items: [{ id: 'skies', tag: 'new' }],
  },
  {
    id: '2026-11-04-zzsoftgl',
    items: [{ id: 'softgl', tag: 'new' }],
  },
  {
    id: '2026-11-04-zcompanions',
    items: [{ id: 'companions', tag: 'fixed' }],
  },
  {
    id: '2026-11-04-troll',
    items: [{ id: 'embers', tag: 'fixed' }],
  },
  {
    id: '2026-11-03-zzzzzstats',
    items: [{ id: 'stats', tag: 'new' }],
  },
  {
    id: '2026-11-03-zzzzreload',
    items: [{ id: 'reload', tag: 'fixed' }],
  },
  {
    id: '2026-11-03-zzzskills',
    items: [{ id: 'skills', tag: 'improved' }],
  },
  {
    id: '2026-11-03-zzfold',
    items: [{ id: 'fold', tag: 'improved' }],
  },
  {
    id: '2026-11-03-zspectres',
    lead: 'crypts',
    items: [{ id: 'crypts', tag: 'new' }],
  },
  {
    id: '2026-11-03-zgifts',
    lead: 'notice',
    items: [
      { id: 'notice', tag: 'new' },
      { id: 'fresh', tag: 'new' },
    ],
  },
  {
    id: '2026-11-03-triangle',
    items: [
      { id: 'square', tag: 'fixed' },
      { id: 'place', tag: 'fixed' },
    ],
  },
  {
    id: '2026-11-03-sides',
    items: [{ id: 'together', tag: 'new' }],
  },
  {
    id: '2026-11-02-zzzzzzsheet',
    items: [{ id: 'sheet', tag: 'fixed' }],
  },
  {
    id: '2026-11-02-zzzzzchat',
    items: [{ id: 'chat', tag: 'fixed' }],
  },
  {
    id: '2026-11-02-zzzzroads',
    items: [{ id: 'roads', tag: 'fixed' }],
  },
  {
    id: '2026-11-02-zzzupdates',
    items: [{ id: 'updates', tag: 'improved' }],
  },
  {
    id: '2026-11-02-zzwarden',
    items: [{ id: 'marks', tag: 'improved' }],
  },
  {
    id: '2026-11-02-zzblanks',
    items: [{ id: 'blanks', tag: 'fixed' }],
  },
  {
    id: '2026-11-02-zautomaton',
    items: [
      { id: 'automaton', tag: 'fixed' },
      { id: 'crypts', tag: 'balance' },
    ],
  },
  {
    id: '2026-11-02-patio',
    items: [{ id: 'patio', tag: 'improved' }],
  },
  {
    id: '2026-11-02-gates',
    items: [{ id: 'gates', tag: 'improved' }],
  },
  {
    id: '2026-11-01-zzzzzzzzzzzzzzzzzzzzzzzzzzzzzznight',
    items: [{ id: 'night', tag: 'new' }],
  },
  {
    id: '2026-11-01-zzzzzzzzzzzzzzzzzzzzzzzzzzzzzaura',
    items: [{ id: 'aura', tag: 'improved' }],
  },
  {
    id: '2026-11-01-zzzzzzzzzzzzzzzzzzzzzzzzzzzzleave',
    items: [{ id: 'leave', tag: 'fixed' }],
  },
  {
    id: '2026-11-01-zzzzzzzzzzzzzzzzzzzzzzzzzzzground',
    items: [{ id: 'ground', tag: 'improved' }],
  },
  {
    id: '2026-11-01-zzzzzzzzzzzzzzzzzzzzzzzzzzfaces',
    items: [{ id: 'faces', tag: 'new' }],
  },
  {
    id: '2026-11-01-zzzzzzzzzzzzzzzzzzzzzzzzzname',
    items: [{ id: 'rename', tag: 'new' }],
  },
  {
    id: '2026-11-01-zzzzzzzzzzzzzzzzzzzzzzzzbell',
    items: [{ id: 'bell', tag: 'new' }],
  },
  {
    id: '2026-11-01-zzzzzzzzzzzzzzzzzzzzzzzgate',
    items: [{ id: 'ticks', tag: 'fixed' }],
  },
  {
    id: '2026-11-01-zzzzzzzzzzzzzzzzzzzzzzearth',
    items: [{ id: 'earth', tag: 'improved' }],
  },
  {
    id: '2026-11-01-zzzzzzzzzzzzzzzzzzzzzhelp',
    items: [{ id: 'help', tag: 'fixed' }],
  },
  {
    id: '2026-11-01-zzzzzzzzzzzzzzzzzzzzhours',
    items: [{ id: 'hours', tag: 'fixed' }],
  },
  {
    id: '2026-11-01-zzzzzzzzzzzzzzzzzzzlid',
    items: [{ id: 'lid', tag: 'new' }],
  },
  {
    id: '2026-11-01-zzzzzzzzzzzzzzzzzzland',
    lead: 'land',
    items: [
      { id: 'land', tag: 'new' },
      { id: 'walls', tag: 'new' },
      { id: 'rifts', tag: 'new' },
      { id: 'golems', tag: 'new' },
      { id: 'big', tag: 'new' },
      { id: 'eras', tag: 'new' },
      { id: 'branches', tag: 'new' },
      { id: 'earth', tag: 'new' },
      { id: 'levels', tag: 'new' },
    ],
  },
  {
    id: '2026-11-01-zzzzzzzzzzzzzzzzzgate',
    items: [{ id: 'gate', tag: 'new' }],
  },
  {
    id: '2026-11-01-zzzzzzzzzzzzzzzzrelics',
    items: [{ id: 'relics', tag: 'balance' }],
  },
  {
    id: '2026-11-01-zzzzzzzzzzzzzzzrace',
    items: [{ id: 'race', tag: 'new' }],
  },
  {
    id: '2026-11-01-zzzzzzzzzzzzzzsunken',
    items: [{ id: 'sunken', tag: 'new' }],
  },
  {
    id: '2026-11-01-zzzzzzzzzzzzzbones',
    items: [{ id: 'bones', tag: 'new' }],
  },
  {
    id: '2026-11-01-zzzzzzzzzzzzmaw',
    items: [{ id: 'maw', tag: 'new' }],
  },
  {
    id: '2026-11-01-zzzzzzzzzzzbond',
    lead: 'bond',
    items: [
      { id: 'bond', tag: 'new' },
      { id: 'expeditions', tag: 'new' },
    ],
  },
  {
    id: '2026-11-01-zzzzzzzzzzforms',
    lead: 'forms',
    items: [
      { id: 'forms', tag: 'new' },
      { id: 'where', tag: 'new' },
    ],
  },
  {
    id: '2026-11-01-zzzzzzzzzfin',
    items: [{ id: 'fin', tag: 'new' }],
  },
  {
    id: '2026-11-01-zzzzzzzzconversion',
    lead: 'turn',
    items: [
      { id: 'turn', tag: 'new' },
      { id: 'bosses', tag: 'new' },
      { id: 'buttons', tag: 'improved' },
      { id: 'looks', tag: 'improved' },
    ],
  },
  {
    id: '2026-11-01-zzzzzzzzashore',
    items: [{ id: 'shore', tag: 'new' }],
  },
  {
    id: '2026-11-01-zzzzzzzbeside',
    items: [{ id: 'beside', tag: 'new' }],
  },
  {
    id: '2026-11-01-zzzzzzglyphs',
    lead: 'glyphs',
    items: [
      { id: 'glyphs', tag: 'new' },
      { id: 'four', tag: 'new' },
      { id: 'stone', tag: 'new' },
    ],
  },
  {
    id: '2026-11-01-zzzzzsima',
    lead: 'sima',
    items: [{ id: 'sima', tag: 'new' }],
  },
  {
    id: '2026-11-01-zzzzzfair',
    items: [{ id: 'fair', tag: 'new' }],
  },
  {
    id: '2026-11-01-zzzzdrinker',
    items: [{ id: 'drinker', tag: 'new' }],
  },
  {
    id: '2026-11-01-zzzwell',
    items: [{ id: 'well', tag: 'new' }],
  },
  {
    id: '2026-11-01-zzrising',
    items: [{ id: 'rising', tag: 'new' }],
  },
  {
    id: '2026-11-01-sheet',
    items: [{ id: 'chat', tag: 'fixed' }],
  },
  {
    id: '2026-11-01-crowd',
    items: [{ id: 'far', tag: 'improved' }],
  },
  {
    id: '2026-10-31-zzzzzzzzzzzzzzzzzzzzzzzzzzzzanvil',
    items: [{ id: 'anvil', tag: 'new' }],
  },
  {
    id: '2026-10-31-zzzzzzzzzzzzzzzzzzzzzzzzzzzlight',
    items: [{ id: 'light', tag: 'improved' }],
  },
  {
    id: '2026-10-31-zzzzzzzzzzzzzzzzzzzzzzzzzreread',
    items: [
      { id: 'back', tag: 'improved' },
      { id: 'ring', tag: 'improved' },
    ],
  },
  {
    id: '2026-10-31-zzzzzzzzzzzzzzzzzzzzzzzzwinder',
    items: [{ id: 'winder', tag: 'new' }],
  },
  {
    id: '2026-10-31-zzzzzzzzzzzzzzzzzzzzzzzpit',
    items: [{ id: 'pit', tag: 'fixed' }],
  },
  {
    id: '2026-10-31-zzzzzzzzzzzzzzzzzzzzzzrails',
    items: [{ id: 'rails', tag: 'new' }],
  },
  {
    id: '2026-10-31-zzzzzzzzzzzzzzzzzzzzzend',
    items: [{ id: 'end', tag: 'fixed' }],
  },
  {
    id: '2026-10-31-zzzzzzzzzzzzzzzzzzzsalidas',
    lead: 'screen',
    items: [
      { id: 'screen', tag: 'new' },
      { id: 'history', tag: 'new' },
    ],
  },
  {
    id: '2026-10-31-zzzzzzzzzzzzzzzzzzbell',
    items: [{ id: 'winch', tag: 'balance' }],
  },
  {
    id: '2026-10-31-zzzzzzzzzzzzzzzzzalone',
    items: [{ id: 'alone', tag: 'new' }],
  },
  {
    id: '2026-10-31-zzzzzzzzzzzzzzzzdragon',
    items: [{ id: 'dragon', tag: 'new' }],
  },
  {
    id: '2026-10-31-zzzzzzzzzzzzzzztower',
    items: [{ id: 'tower', tag: 'new' }],
  },
  {
    id: '2026-10-31-zzzzzzzzzzzzzzworks',
    items: [{ id: 'hint', tag: 'improved' }],
  },
  {
    id: '2026-10-31-zzzzzzzzzzzzztrap',
    items: [{ id: 'trap', tag: 'new' }],
  },
  {
    id: '2026-10-31-zzzzzzzzzzzzcamera',
    items: [{ id: 'drag', tag: 'fixed' }],
  },
  {
    id: '2026-10-31-zzzzzzzzzzzpaladin',
    items: [
      { id: 'smite', tag: 'balance' },
      { id: 'hallow', tag: 'balance' },
    ],
  },
  {
    id: '2026-10-31-zzzzzzzzzzcamp',
    items: [{ id: 'camp', tag: 'new' }],
  },
  {
    id: '2026-10-31-zzzzzzzzzfear',
    items: [{ id: 'fear', tag: 'improved' }],
  },
  {
    id: '2026-10-31-zzzzzzzzzburn',
    items: [{ id: 'burn', tag: 'new' }],
  },
  {
    id: '2026-10-31-zzzzzzzzwaves',
    items: [{ id: 'waves', tag: 'improved' }],
  },
  {
    id: '2026-10-31-zzzzzzzbet',
    items: [{ id: 'bet', tag: 'new' }],
  },
  {
    id: '2026-10-31-zzzzzzcrypts',
    items: [
      { id: 'rooms', tag: 'balance' },
      { id: 'guardians', tag: 'balance' },
    ],
  },
  {
    id: '2026-10-31-zzzzalone',
    items: [{ id: 'alone', tag: 'new' }],
  },
  {
    id: '2026-10-31-zzzcrypts',
    items: [
      { id: 'fights', tag: 'improved' },
      { id: 'gear', tag: 'fixed' },
      { id: 'crypt', tag: 'improved' },
      { id: 'start', tag: 'fixed' },
      { id: 'colossus', tag: 'fixed' },
      { id: 'blur', tag: 'improved' },
      { id: 'corner', tag: 'fixed' },
    ],
  },
  {
    id: '2026-10-31-zzhunt',
    items: [{ id: 'hunt', tag: 'new' }],
  },
  {
    id: '2026-10-31-zlighter',
    items: [
      { id: 'bastion', tag: 'improved' },
      { id: 'waves', tag: 'fixed' },
      { id: 'panel', tag: 'improved' },
    ],
  },
  {
    id: '2026-10-31-heroes',
    items: [{ id: 'swap', tag: 'fixed' }],
  },
  {
    id: '2026-10-30-zzzzzzzzzzzzzzzzzzcrypt',
    items: [
      { id: 'guardians', tag: 'fixed' },
      { id: 'deep', tag: 'improved' },
    ],
  },
  {
    id: '2026-10-30-zzzzzzzzzzzzzzzzzlash',
    items: [
      { id: 'lash', tag: 'new' },
      { id: 'toasts', tag: 'fixed' },
    ],
  },
  {
    id: '2026-10-30-zzzzzzzzzzzzzzzzclasses',
    lead: 'skills',
    items: [
      { id: 'skills', tag: 'balance' },
      { id: 'paladin', tag: 'new' },
      { id: 'ranger', tag: 'improved' },
      { id: 'warrior', tag: 'new' },
      { id: 'barbarian', tag: 'balance' },
      { id: 'artificer', tag: 'balance' },
      { id: 'cleric', tag: 'balance' },
      { id: 'ghosts', tag: 'improved' },
      { id: 'guardians', tag: 'balance' },
    ],
  },
  {
    id: '2026-10-30-zzzzzzzzzzzzzzzhorn',
    lead: 'horn',
    items: [{ id: 'horn', tag: 'new' }],
  },
  {
    id: '2026-10-30-zzzzzzzzzzzzzzsignals',
    lead: 'signals',
    items: [
      { id: 'signals', tag: 'new' },
      { id: 'help', tag: 'new' },
    ],
  },
  {
    id: '2026-10-30-zzzzzzzzzzzzzmada',
    items: [{ id: 'mada', tag: 'new' }],
  },
  {
    id: '2026-10-30-zzzzzzzzzzzzbar',
    items: [{ id: 'bar', tag: 'fixed' }, { id: 'phone', tag: 'improved' }],
  },
  {
    id: '2026-10-30-zzzzzzzzzzzchat',
    items: [{ id: 'when', tag: 'improved' }],
  },
  {
    id: '2026-10-30-zzzzzzzzzzdamage',
    items: [{ id: 'damage', tag: 'improved' }],
  },
  {
    id: '2026-10-30-zzzzzzzzzaccuse',
    items: [{ id: 'accuse', tag: 'new' }],
  },
  {
    id: '2026-10-30-zzzzzzzzshadow',
    items: [{ id: 'shadow', tag: 'new' }],
  },
  {
    id: '2026-10-30-zzzzzzzflee',
    items: [{ id: 'flee', tag: 'improved' }],
  },
  {
    id: '2026-10-30-zzzzzzchat',
    items: [{ id: 'chat', tag: 'fixed' }],
  },
  {
    id: '2026-10-30-zzzzzfaces',
    lead: 'faces',
    items: [{ id: 'faces', tag: 'new' }],
  },
  {
    id: '2026-10-30-zzzzbets',
    lead: 'bets',
    items: [{ id: 'bets', tag: 'new' }],
  },
  {
    id: '2026-10-30-zzzheal',
    items: [{ id: 'heal', tag: 'fixed' }],
  },
  {
    id: '2026-10-30-zzicons',
    items: [{ id: 'icons', tag: 'fixed' }],
  },
  {
    id: '2026-10-30-zhands',
    items: [{ id: 'hands', tag: 'fixed' }],
  },
  {
    id: '2026-10-30-zforge',
    items: [{ id: 'ruined', tag: 'fixed' }],
  },
  {
    id: '2026-10-30-works',
    items: [{ id: 'works', tag: 'new' }],
  },
  {
    id: '2026-10-29-zzzzzzzzzzzzzzzzzzzzzbear',
    items: [{ id: 'bear', tag: 'fixed' }],
  },
  {
    id: '2026-10-29-zzzzzzzzzzzzzzzzzzzzpanel',
    items: [{ id: 'puzzle', tag: 'improved' }],
  },
  {
    id: '2026-10-29-zzzzzzzzzzzzzzzzzzzfoes',
    items: [{ id: 'count', tag: 'fixed' }],
  },
  {
    id: '2026-10-29-zzzzzzzzzzzzzzzzzzdeeds',
    lead: 'deeds',
    items: [
      { id: 'deeds', tag: 'new' },
      { id: 'hall', tag: 'new' },
    ],
  },
  {
    id: '2026-10-29-zzzzzzzzzzzzzzzzzchat',
    items: [{ id: 'keyboard', tag: 'fixed' }],
  },
  {
    id: '2026-10-29-zzzzzzzzzzzzzzzzeight',
    lead: 'eight',
    items: [
      { id: 'eight', tag: 'new' },
      { id: 'scale', tag: 'balance' },
    ],
  },
  {
    id: '2026-10-29-zzzzzzzzzzzzzzzstart',
    items: [
      { id: 'start', tag: 'improved' },
      { id: 'apart', tag: 'improved' },
    ],
  },
  {
    id: '2026-10-29-zzzzzzzzzzzzzzmilitary',
    items: [{ id: 'iron', tag: 'fixed' }],
  },
  {
    id: '2026-10-29-zzzzzzzzzzzzzcall',
    items: [{ id: 'call', tag: 'improved' }],
  },
  {
    id: '2026-10-29-zzzzzzzzzzzzchat',
    items: [{ id: 'chat', tag: 'improved' }],
  },
  {
    id: '2026-10-29-zzzzzzzzzzzstay',
    items: [{ id: 'stay', tag: 'fixed' }],
  },
  {
    id: '2026-10-29-zzzzzzzzzznames',
    lead: 'names',
    items: [{ id: 'names', tag: 'new' }],
  },
  {
    id: '2026-10-29-zzzzzzzzzlong',
    lead: 'sets',
    items: [
      { id: 'sets', tag: 'new' },
      { id: 'forge', tag: 'new' },
    ],
  },
  {
    id: '2026-10-29-zzzzzzzzarmory',
    lead: 'rack',
    items: [{ id: 'rack', tag: 'new' }],
  },
  {
    id: '2026-10-29-zzzzzzzloot',
    lead: 'group',
    items: [
      { id: 'group', tag: 'new' },
      { id: 'places', tag: 'new' },
      { id: 'bag', tag: 'improved' },
    ],
  },
  {
    id: '2026-10-29-zzzzzzgems',
    lead: 'settings',
    items: [
      { id: 'settings', tag: 'new' },
      { id: 'gems', tag: 'new' },
    ],
  },
  {
    id: '2026-10-29-zzzzzlook',
    items: [{ id: 'hands', tag: 'new' }],
  },
  {
    id: '2026-10-29-zzzzwork',
    items: [{ id: 'help', tag: 'fixed' }],
  },
  {
    id: '2026-10-29-zzzgear',
    lead: 'slots',
    items: [
      { id: 'slots', tag: 'new' },
      { id: 'bases', tag: 'new' },
      { id: 'power', tag: 'new' },
      { id: 'sheet', tag: 'improved' },
    ],
  },
  {
    id: '2026-10-29-zzsmooth',
    items: [
      { id: 'waves', tag: 'improved' },
      { id: 'sheet', tag: 'improved' },
    ],
  },
  {
    id: '2026-10-29-zsheet',
    lead: 'tabs',
    items: [
      { id: 'tabs', tag: 'improved' },
      { id: 'forge', tag: 'fixed' },
    ],
  },
  {
    id: '2026-10-29-zheroes',
    lead: 'roster',
    items: [{ id: 'roster', tag: 'new' }],
  },
  {
    id: '2026-10-29-slingers',
    items: [{ id: 'stuck', tag: 'fixed' }],
  },
  {
    id: '2026-10-29-rooms',
    items: [{ id: 'together', tag: 'improved' }],
  },
  {
    id: '2026-10-29-bastion',
    items: [{ id: 'whole', tag: 'fixed' }],
  },
  {
    id: '2026-10-28-zzzzzzzzzzzzzzzzzzzzzzzzzzzzzpower',
    items: [{ id: 'power', tag: 'improved' }],
  },
  {
    id: '2026-10-28-zzzzzzzzzzzzzzzzzzzzzzzzzzzzstart',
    items: [{ id: 'smooth', tag: 'improved' }],
  },
  {
    id: '2026-10-28-zzzzzzzzzzzzzzzzzzzzzzzzzzzsomnhold',
    lead: 'era5',
    items: [
      { id: 'era5', tag: 'new' },
      { id: 'beacon', tag: 'new' },
      { id: 'nightmares', tag: 'new' },
      { id: 'towers5', tag: 'fixed' },
    ],
  },
  {
    id: '2026-10-28-zzzzzzzzzzzzzzzzzzzzzzzzzfonts',
    items: [{ id: 'fonts', tag: 'fixed' }],
  },
  {
    id: '2026-10-28-zzzzzzzzzzzzzzzzzzzzzzzzperk',
    items: [{ id: 'total', tag: 'fixed' }],
  },
  {
    id: '2026-10-28-zzzzzzzzzzzzzzzzzzzzzzznightmares',
    lead: 'nightmares',
    items: [{ id: 'nightmares', tag: 'new' }],
  },
  {
    id: '2026-10-28-zzzzzzzzzzzzzzzzzzzzzzzcarry',
    items: [{ id: 'buttons', tag: 'fixed' }],
  },
  {
    id: '2026-10-28-zzzzzzzzzzzzzzzzzzzzzzwatch',
    lead: 'trebuchet',
    items: [
      { id: 'trebuchet', tag: 'new' },
      { id: 'watchtower', tag: 'new' },
      { id: 'sanctum', tag: 'new' },
    ],
  },
  {
    id: '2026-10-28-zzzzzzzzzzzzzzzzzzzzzzmemory',
    items: [{ id: 'lighter', tag: 'improved' }],
  },
  {
    id: '2026-10-28-zzzzzzzzzzzzzzzzzzzzzrooms',
    items: [{ id: 'smooth', tag: 'improved' }],
  },
  {
    id: '2026-10-28-zzzzzzzzzzzzzzzzzzzzeraiv',
    lead: 'era4',
    items: [
      { id: 'era4', tag: 'new' },
      { id: 'forge5', tag: 'new' },
      { id: 'towers5', tag: 'new' },
    ],
  },
  {
    id: '2026-10-28-zzzzzzzzzzzzzzzzzzzhelp',
    items: [{ id: 'boxed', tag: 'fixed' }],
  },
  {
    id: '2026-10-28-zzzzzzzzzzzzzzzzzzstages',
    lead: 'stages',
    items: [
      { id: 'stages', tag: 'improved' },
      { id: 'last', tag: 'balance' },
    ],
  },
  {
    id: '2026-10-28-zzzzzzzzzzzzzzzzzsaga',
    items: [
      { id: 'level', tag: 'improved' },
      { id: 'kept', tag: 'new' },
    ],
  },
  {
    id: '2026-10-28-zzzzzzzzzzzzzzzzlive',
    items: [{ id: 'live', tag: 'balance' }],
  },
  {
    id: '2026-10-28-zzzzzzzzzzzzzzzbulk',
    items: [{ id: 'bulk', tag: 'balance' }],
  },
  {
    id: '2026-10-28-zzzzzzzzzzzzzzlevel',
    items: [{ id: 'level', tag: 'improved' }],
  },
  {
    id: '2026-10-28-zzzzzzzzzzzzzawaken',
    lead: 'awaken',
    items: [
      { id: 'awaken', tag: 'new' },
      { id: 'seals', tag: 'new' },
    ],
  },
  {
    id: '2026-10-28-zzzzzzzzzzzzskills',
    items: [{ id: 'said', tag: 'improved' }],
  },
  {
    id: '2026-10-28-zzzzzzzzzzzforge',
    lead: 'keep',
    items: [
      { id: 'group', tag: 'improved' },
      { id: 'forge', tag: 'new' },
      { id: 'keep', tag: 'new' },
    ],
  },
  {
    id: '2026-10-28-zzzzzzzzzzzcaps',
    items: [{ id: 'caps', tag: 'improved' }],
  },
  {
    id: '2026-10-28-zzzzzzzzzzrooms',
    items: [{ id: 'rooms', tag: 'improved' }],
  },
  {
    id: '2026-10-28-zzzzzzzzzexit',
    items: [{ id: 'exit', tag: 'fixed' }],
  },
  {
    id: '2026-10-28-zzzzzzzztide',
    lead: 'tide',
    items: [
      { id: 'tide', tag: 'new' },
      { id: 'repeat', tag: 'new' },
      { id: 'xp', tag: 'balance' },
    ],
  },
  {
    id: '2026-10-28-zzzzzzzmail',
    items: [{ id: 'mail', tag: 'improved' }],
  },
  {
    id: '2026-10-28-zzzzzzhitches',
    items: [{ id: 'hitches', tag: 'fixed' }],
  },
  {
    id: '2026-10-28-zzzzzmammoth',
    items: [{ id: 'automaton', tag: 'fixed' }],
  },
  {
    id: '2026-10-28-zzzztomes',
    items: [{ id: 'pages', tag: 'fixed' }],
  },
  {
    id: '2026-10-28-zzzkeep',
    items: [{ id: 'keep', tag: 'fixed' }],
  },
  {
    id: '2026-10-28-zzsiege',
    items: [{ id: 'ghosts', tag: 'improved' }],
  },
  {
    id: '2026-10-28-zailments',
    lead: 'marks',
    items: [
      { id: 'marks', tag: 'new' },
      { id: 'cleric', tag: 'improved' },
      { id: 'ash', tag: 'balance' },
    ],
  },
  {
    id: '2026-10-28-chat',
    items: [{ id: 'sheet', tag: 'fixed' }],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzzzzzzzzzzzzzzzzzzzzlights',
    items: [{ id: 'freeze', tag: 'fixed' }],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzzzzzzzzzzzzzzzzzzzscreen',
    items: [{ id: 'gl', tag: 'fixed' }],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzzzzzzzzzzzzzzzzzzhelpers',
    items: [{ id: 'unique', tag: 'improved' }],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzzzzzzzzzzzzzzzzzmammoth',
    items: [{ id: 'march', tag: 'fixed' }],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzzzzzzzzzzzzzzzzmidfight',
    items: [{ id: 'midfight', tag: 'fixed' }],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzzzzzzzzzzzzzzzways',
    lead: 'map',
    items: [
      { id: 'map', tag: 'improved' },
      { id: 'how', tag: 'new' },
      { id: 'fallen', tag: 'improved' },
      { id: 'rope', tag: 'improved' },
      { id: 'stairs', tag: 'improved' },
      { id: 'levers', tag: 'fixed' },
    ],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzzzzzzzzzzzzzzend',
    items: [{ id: 'end', tag: 'fixed' }],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzzzzzzzzzzzzzbell',
    items: [
      { id: 'bell', tag: 'fixed' },
      { id: 'tome', tag: 'fixed' },
    ],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzzzzzzzzzzzzbuilt',
    items: [{ id: 'built', tag: 'improved' }],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzzzzzzzzzzzjoin',
    items: [{ id: 'join', tag: 'improved' }],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzzzzzzzzzzfloors',
    items: [
      { id: 'floors', tag: 'fixed' },
      { id: 'map', tag: 'fixed' },
    ],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzzzzzzzzzcosts',
    items: [{ id: 'costs', tag: 'fixed' }],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzzzzzzzzcrowds',
    items: [{ id: 'crowds', tag: 'improved' }],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzzzzzzznumbers',
    items: [{ id: 'numbers', tag: 'fixed' }],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzzzzzzvote',
    items: [{ id: 'vote', tag: 'fixed' }],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzzzzzzsmooth',
    items: [{ id: 'smooth', tag: 'improved' }],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzzzzzthird',
    lead: 'skills',
    items: [
      { id: 'skills', tag: 'new' },
      { id: 'ults', tag: 'new' },
    ],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzzzzzresume',
    items: [{ id: 'resume', tag: 'fixed' }],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzzzzults',
    lead: 'skills',
    items: [{ id: 'skills', tag: 'new' }],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzzzzswarm',
    items: [{ id: 'swarm', tag: 'improved' }],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzzzmid',
    lead: 'skills',
    items: [{ id: 'skills', tag: 'new' }],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzfurrowsfix',
    items: [
      { id: 'walk', tag: 'fixed' },
      { id: 'doors', tag: 'fixed' },
    ],
  },
  {
    id: '2026-10-27-zzzzzzzzzzzchest',
    items: [{ id: 'chest', tag: 'improved' }],
  },
  {
    id: '2026-10-27-zzzzzzzzzztable',
    items: [{ id: 'table', tag: 'improved' }],
  },
  {
    id: '2026-10-27-zzzzzzzzzvariants',
    items: [
      { id: 'variant', tag: 'fixed' },
      { id: 'numbers', tag: 'fixed' },
    ],
  },
  {
    id: '2026-10-27-zzzzzzzzpaths',
    items: [{ id: 'walk', tag: 'fixed' }],
  },
  {
    id: '2026-10-27-zzzzzzzfurrows',
    lead: 'furrows',
    items: [{ id: 'furrows', tag: 'new' }],
  },
  {
    id: '2026-10-27-zzzzzzzftomes',
    lead: 'tomes',
    items: [
      { id: 'tomes', tag: 'new' },
      { id: 'skills', tag: 'new' },
      { id: 'shelf', tag: 'new' },
    ],
  },
  {
    id: '2026-10-27-zzzzzzzeshow',
    lead: 'ult',
    items: [
      { id: 'ult', tag: 'improved' },
      { id: 'glow', tag: 'improved' },
      { id: 'combos', tag: 'new' },
    ],
  },
  {
    id: '2026-10-27-zzzzzzzdults',
    lead: 'skills',
    items: [{ id: 'skills', tag: 'new' }],
  },
  {
    id: '2026-10-27-zzzzzzzbatch',
    lead: 'skills',
    items: [{ id: 'skills', tag: 'new' }],
  },
  {
    id: '2026-10-27-zzzzzzreact',
    lead: 'react',
    items: [{ id: 'react', tag: 'new' }],
  },
  {
    id: '2026-10-27-zzzzzdrone',
    items: [{ id: 'skills', tag: 'new' }],
  },
  {
    id: '2026-10-27-zzzzlearned',
    lead: 'skills',
    items: [
      { id: 'skills', tag: 'new' },
      { id: 'spectres', tag: 'improved' },
    ],
  },
  {
    id: '2026-10-27-zzztree',
    items: [{ id: 'tree', tag: 'improved' }],
  },
  {
    id: '2026-10-27-zzzbraziers',
    items: [{ id: 'braziers', tag: 'fixed' }],
  },
  {
    id: '2026-10-27-zzpowers',
    lead: 'carry',
    items: [
      { id: 'carry', tag: 'new' },
      { id: 'tree', tag: 'improved' },
      { id: 'ranks', tag: 'balance' },
    ],
  },
  {
    id: '2026-10-27-zcompare',
    items: [{ id: 'compare', tag: 'improved' }],
  },
  {
    id: '2026-10-27-plunder',
    lead: 'plunder',
    items: [
      { id: 'plunder', tag: 'new' },
      { id: 'runs', tag: 'improved' },
    ],
  },
  {
    id: '2026-10-27-mammoth',
    lead: 'mammoth',
    items: [{ id: 'mammoth', tag: 'new' }],
  },
  {
    id: '2026-10-27-general',
    items: [
      { id: 'army', tag: 'new' },
      { id: 'moves', tag: 'improved' },
    ],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzsagaharder',
    items: [{ id: 'saga', tag: 'balance' }],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzuniques',
    lead: 'uniques',
    items: [
      { id: 'uniques', tag: 'improved' },
      { id: 'thorns', tag: 'fixed' },
    ],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzsecretplans',
    items: [{ id: 'secret', tag: 'improved' }],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzyields',
    items: [{ id: 'yields', tag: 'improved' }],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzskillcard',
    items: [{ id: 'card', tag: 'fixed' }],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzmoreplans',
    items: [
      { id: 'rush', tag: 'new' },
      { id: 'pincer', tag: 'new' },
    ],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzgeneral',
    items: [{ id: 'general', tag: 'improved' }],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzrerun',
    items: [{ id: 'rerun', tag: 'improved' }],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzplans',
    lead: 'plans',
    items: [{ id: 'plans', tag: 'new' }],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzharness',
    items: [{ id: 'harness', tag: 'improved' }],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzvote',
    lead: 'rerun',
    items: [
      { id: 'rerun', tag: 'new' },
      { id: 'vote', tag: 'new' },
    ],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzzzzzzzzzzzzcellars',
    lead: 'cellars',
    items: [
      { id: 'cellars', tag: 'new' },
      { id: 'carry', tag: 'new' },
      { id: 'trophy', tag: 'new' },
    ],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzzzzzzzzzzzsiege',
    lead: 'mangonel',
    items: [
      { id: 'mangonel', tag: 'new' },
      { id: 'siege', tag: 'balance' },
      { id: 'archer', tag: 'balance' },
    ],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzzzzzzzzzblade',
    items: [{ id: 'gateBlade', tag: 'fixed' }],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzzzzzzzzphone',
    items: [{ id: 'cool', tag: 'improved' }],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzzzzzzzend',
    items: [{ id: 'outcome', tag: 'improved' }],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzzzzzzquests',
    lead: 'uniques',
    items: [
      { id: 'uniques', tag: 'improved' },
      { id: 'story', tag: 'improved' },
      { id: 'flow', tag: 'improved' },
    ],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzzzzzgpu',
    items: [
      { id: 'memory', tag: 'fixed' },
      { id: 'waveBack', tag: 'fixed' },
    ],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzzzzpack',
    items: [{ id: 'shove', tag: 'balance' }],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzzzlive',
    lead: 'steady',
    items: [
      { id: 'steady', tag: 'fixed' },
      { id: 'leader', tag: 'fixed' },
      { id: 'lobby', tag: 'fixed' },
      { id: 'clear', tag: 'improved' },
    ],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzzrejoin',
    items: [{ id: 'rejoin', tag: 'fixed' }],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzzgear',
    lead: 'gearDamage',
    items: [{ id: 'gearDamage', tag: 'balance' }],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzzfind',
    items: [
      { id: 'find', tag: 'improved' },
      { id: 'cursor', tag: 'improved' },
      { id: 'chatSpot', tag: 'fixed' },
    ],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzzsupport',
    lead: 'bloom',
    items: [
      { id: 'bloom', tag: 'new' },
      { id: 'auras', tag: 'new' },
      { id: 'inspire', tag: 'improved' },
      { id: 'potency', tag: 'improved' },
    ],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzzcaravan',
    lead: 'caravan',
    items: [
      { id: 'caravan', tag: 'fixed' },
      { id: 'balance', tag: 'balance' },
    ],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzzcrypt',
    items: [
      { id: 'lobby', tag: 'fixed' },
      { id: 'eraTag', tag: 'fixed' },
    ],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzzogre',
    items: [{ id: 'shove', tag: 'fixed' }],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzzrampart',
    items: [{ id: 'rampartStone', tag: 'fixed' }],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzzkeep',
    lead: 'keepChat',
    items: [
      { id: 'keepChat', tag: 'new' },
      { id: 'paths', tag: 'new' },
      { id: 'enterChat', tag: 'improved' },
    ],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzscout',
    items: [{ id: 'scout', tag: 'improved' }],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzzlive',
    items: [
      { id: 'stale', tag: 'fixed' },
      { id: 'helpDistrict', tag: 'fixed' },
    ],
  },
  {
    id: '2026-10-26-zzzzzzzzzzzailments',
    lead: 'ailments',
    items: [
      { id: 'ailments', tag: 'new' },
      { id: 'ward', tag: 'new' },
    ],
  },
  {
    id: '2026-10-26-zzzzzzzzzzbattery',
    items: [{ id: 'battery', tag: 'improved' }],
  },
  {
    id: '2026-10-26-zzzzzzzzzzattune',
    lead: 'attune',
    items: [
      { id: 'attune', tag: 'new' },
      { id: 'omenType', tag: 'improved' },
    ],
  },
  {
    id: '2026-10-26-zzzzzzzzzfate',
    items: [{ id: 'fateDie', tag: 'balance' }],
  },
  {
    id: '2026-10-26-zzzzzzzzdamage',
    lead: 'types',
    items: [
      { id: 'types', tag: 'new' },
      { id: 'affinity', tag: 'new' },
      { id: 'wood', tag: 'balance' },
    ],
  },
  {
    id: '2026-10-26-zzzzzzzoverload',
    items: [{ id: 'overload', tag: 'improved' }],
  },
  {
    id: '2026-10-26-zzzzzzghosts',
    items: [
      { id: 'replays', tag: 'fixed' },
      { id: 'posts', tag: 'improved' },
      { id: 'woods', tag: 'fixed' },
    ],
  },
  {
    id: '2026-10-26-zzzzztitan',
    lead: 'era3',
    items: [
      { id: 'era3', tag: 'new' },
      { id: 'giantStep', tag: 'new' },
      { id: 'conditions', tag: 'new' },
    ],
  },
  {
    id: '2026-10-26-zzzzcitadel',
    lead: 'era2',
    items: [
      { id: 'era2', tag: 'new' },
      { id: 'outside', tag: 'new' },
    ],
  },
  {
    id: '2026-10-26-zzzwavexp',
    items: [{ id: 'waveXp', tag: 'improved' }],
  },
  {
    id: '2026-10-26-zzskills',
    lead: 'skillLook',
    items: [{ id: 'skillLook', tag: 'improved' }],
  },
  {
    id: '2026-10-26-zcreatures',
    lead: 'creatures',
    items: [
      { id: 'creatures', tag: 'new' },
      { id: 'hosts', tag: 'new' },
      { id: 'delves', tag: 'new' },
      { id: 'crits', tag: 'new' },
    ],
  },
  {
    id: '2026-10-26',
    items: [
      { id: 'omenSky', tag: 'improved' },
      { id: 'helpPhone', tag: 'fixed' },
    ],
  },
  {
    id: '2026-10-25-zzzzglass',
    items: [{ id: 'glass', tag: 'fixed' }],
  },
  {
    id: '2026-10-25-zzzlive',
    lead: 'together',
    items: [{ id: 'together', tag: 'balance' }],
  },
  {
    id: '2026-10-25-zzdust',
    lead: 'price',
    items: [
      { id: 'price', tag: 'improved' },
      { id: 'log', tag: 'improved' },
    ],
  },
  {
    id: '2026-10-25-zparty',
    items: [{ id: 'strip', tag: 'improved' }],
  },
  {
    id: '2026-10-25-wide',
    items: [{ id: 'rooms', tag: 'fixed' }],
  },
  {
    id: '2026-10-25-way',
    lead: 'way',
    items: [{ id: 'way', tag: 'improved' }],
  },
  {
    id: '2026-10-25-tide',
    lead: 'ink',
    items: [{ id: 'ink', tag: 'balance' }],
  },
  {
    id: '2026-10-25-target',
    lead: 'aim',
    items: [
      { id: 'aim', tag: 'improved' },
      { id: 'hover', tag: 'improved' },
    ],
  },
  {
    id: '2026-10-25-armory',
    lead: 'effects',
    items: [
      { id: 'effects', tag: 'new' },
      { id: 'reforge', tag: 'new' },
      { id: 'gems', tag: 'new' },
      { id: 'awaken', tag: 'new' },
      { id: 'offerings', tag: 'new' },
      { id: 'pace', tag: 'new' },
      { id: 'board', tag: 'new' },
      { id: 'gifts', tag: 'improved' },
      { id: 'cards', tag: 'improved' },
      { id: 'buildCards', tag: 'improved' },
      { id: 'waitRoom', tag: 'improved' },
      { id: 'descend', tag: 'improved' },
      { id: 'glassBridge', tag: 'fixed' },
      { id: 'meleeChase', tag: 'improved' },
      { id: 'stack', tag: 'balance' },
    ],
  },
  {
    id: '2026-10-25',
    lead: 'obsidian',
    items: [
      { id: 'obsidian', tag: 'new' },
      { id: 'saga', tag: 'new' },
      { id: 'omens', tag: 'new' },
      { id: 'cap', tag: 'improved' },
      { id: 'lag', tag: 'improved' },
      { id: 'thorns', tag: 'fixed' },
      { id: 'deepStuck', tag: 'fixed' },
      { id: 'cryptScroll', tag: 'fixed' },
    ],
  },
  {
    id: '2026-10-24',
    lead: 'classes',
    items: [
      { id: 'classes', tag: 'new' },
      { id: 'ascuas', tag: 'new' },
      { id: 'eras', tag: 'new' },
      { id: 'push', tag: 'new' },
      { id: 'halloween', tag: 'new' },
      { id: 'smooth', tag: 'improved' },
      { id: 'minis', tag: 'improved' },
      { id: 'repairAll', tag: 'improved' },
      { id: 'mercs', tag: 'improved' },
      { id: 'relics', tag: 'improved' },
      { id: 'rareFx', tag: 'fixed' },
    ],
  },
  {
    id: '2026-10-23',
    lead: 'tree',
    items: [
      { id: 'tree', tag: 'new' },
      { id: 'cleric', tag: 'new' },
      { id: 'classChange', tag: 'new' },
      { id: 'countdown', tag: 'improved' },
      { id: 'golems', tag: 'improved' },
      { id: 'ranges', tag: 'improved' },
      { id: 'forge', tag: 'improved' },
      { id: 'names', tag: 'improved' },
      { id: 'ghosts', tag: 'improved' },
      { id: 'walls', tag: 'fixed' },
      { id: 'loot', tag: 'fixed' },
      { id: 'flyers', tag: 'fixed' },
    ],
  },
];
